import { useState, useCallback, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { getBreadcrumbs, getNavSections, isStrategyBuilderPath } from '@/lib/navigation';
import Topbar from '@/components/layout/Topbar';
import Sidebar from '@/components/layout/Sidebar';
import MobileDrawer from '@/components/layout/MobileDrawer';
import { useAuth } from '@/contexts/AuthContext';
import { useEntityBreadcrumb } from '@/contexts/EntityBreadcrumbContext';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { DISCARD_WORKSPACE_CHANGES_EVENT } from '@/lib/workspaceNavigation';

const SIDEBAR_STORAGE_KEY = 'megatraderdx.sidebar-collapsed';

export interface AppShellOutletContext {
  setWorkspaceDirty: (dirty: boolean) => void;
}

function initialSidebarCollapsed() {
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export default function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(initialSidebarCollapsed);
  const [workspaceDirty, setWorkspaceDirty] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { entityLabel, setEntityLabel } = useEntityBreadcrumb();
  const navSections = getNavSections(user?.role);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const toggleSidebar = useCallback(() => setSidebarCollapsed((prev) => !prev), []);

  const activePath = location.pathname;
  const workspace = isStrategyBuilderPath(activePath);
  const breadcrumbs = getBreadcrumbs(activePath, entityLabel ?? undefined);

  useEffect(() => setDrawerOpen(false), [activePath]);
  useEffect(() => setEntityLabel(null), [activePath, setEntityLabel]);
  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarCollapsed));
    } catch {
      // Storage can be unavailable in hardened or private browser contexts.
    }
  }, [sidebarCollapsed]);

  const requestLogout = useCallback(() => {
    setDrawerOpen(false);
    if (workspaceDirty) setConfirmLogout(true);
    else void logout();
  }, [logout, workspaceDirty]);

  return (
    <div className={`app-shell${sidebarCollapsed ? ' app-shell--collapsed' : ''}`}>
      <div className="app-shell__topbar">
        <Topbar
          onDrawerToggle={() => setDrawerOpen(true)}
          drawerOpen={drawerOpen}
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={toggleSidebar}
          activePath={activePath}
          breadcrumbs={breadcrumbs}
          onLogout={requestLogout}
        />
      </div>

      <div className="app-shell__sidebar">
        <Sidebar
          sections={navSections}
          activePath={activePath}
          collapsed={sidebarCollapsed}
        />
      </div>

      <MobileDrawer
        sections={navSections}
        activePath={activePath}
        isOpen={drawerOpen}
        onClose={closeDrawer}
        onLogout={requestLogout}
      />

      <div className="app-shell__body">
        <main className={`app-shell__main${workspace ? ' app-shell__main--workspace' : ''}`} id="main-content">
          <div className={`app-shell__page${workspace ? ' app-shell__page--workspace' : ''}`}>
            <Outlet context={{ setWorkspaceDirty }} />
          </div>
        </main>
      </div>

      {confirmLogout && (
        <Dialog
          title="Discard unsaved changes?"
          onClose={() => setConfirmLogout(false)}
          actions={(
            <>
              <Button type="button" variant="ghost" onClick={() => setConfirmLogout(false)}>Keep editing</Button>
              <Button
                type="button"
                variant="danger"
                onClick={() => {
                  window.dispatchEvent(new Event(DISCARD_WORKSPACE_CHANGES_EVENT));
                  setWorkspaceDirty(false);
                  setConfirmLogout(false);
                  void logout();
                }}
              >
                Discard and sign out
              </Button>
            </>
          )}
        >
          <p>Your strategy has changes that have not been saved.</p>
        </Dialog>
      )}
    </div>
  );
}
